import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { GameHud } from '@/components/GameHud';
import { GlassButton } from '@/components/GlassButton';
import { GlassCard } from '@/components/GlassCard';
import { TapToAsk } from '@/components/TapToAsk';
import { Icon } from '@/components/Icon';
import { sfx } from '@/lib/audio';
import { grade } from '@/lib/score';
import { shuffle, tokenize, useAppStore } from '@/lib/store';
import { ModeSwitcher } from './ModeSwitcher';
import { StudyHeader } from './StudyHeader';
import { useStudyLesson } from './useStudyLesson';

export function PuzzlePage(): JSX.Element {
  const { ready, notFoundView, study } = useStudyLesson('puzzle');
  if (!ready || study === null) return notFoundView ?? <div />;

  const { course, lesson, sentence, index, total, progressPct, setProgressBoost,
          next, submitGrade, markComplete, bumpStreak, bumpMastery, recordMistake } = study;

  const masteryLvl = useAppStore((s) => s.mastery[sentence.id] ?? 0);
  const correctTokens = useMemo(() => tokenize(sentence.en), [sentence.en]);

  // L2+ adds distractor words — dedup to avoid double "a" in pool.
  const distractorCount = masteryLvl >= 2 ? 2 : 0;
  const distractors = useMemo(() => {
    if (distractorCount === 0) return [];
    const blacklist = new Set(correctTokens.map((t) => t.toLowerCase().replace(/[^a-z']/g, '')));
    const pool = ['was', 'is', 'are', 'to', 'the', 'of', 'in', 'on', 'and', 'have', 'do', 'will'];
    return shuffle(pool.filter((c) => !blacklist.has(c))).slice(0, distractorCount);
  }, [distractorCount, correctTokens]);

  const [pool, setPool] = useState<string[]>(() => shuffle([...correctTokens, ...distractors]));
  const [placed, setPlaced] = useState<string[]>([]);
  const [status, setStatus] = useState<'pending' | 'correct' | 'wrong'>('pending');
  const [showHint, setShowHint] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const startTime = useRef(Date.now());

  useEffect(() => {
    setPool(shuffle([...correctTokens, ...distractors]));
    setPlaced([]);
    setStatus('pending');
    setShowHint(false);
    setErrorCount(0);
    startTime.current = Date.now();
  }, [correctTokens, distractors, sentence.id]);

  function pickFromPool(token: string, poolIdx: number): void {
    if (status !== 'pending') return;
    sfx.tilePick();
    const newPool = pool.slice();
    newPool.splice(poolIdx, 1);
    setPool(newPool);
    setPlaced([...placed, token]);
    setTimeout(() => sfx.tileDrop(), 60);
  }

  function returnToPool(token: string, placedIdx: number): void {
    if (status !== 'pending') return;
    sfx.click();
    const newPlaced = placed.slice();
    newPlaced.splice(placedIdx, 1);
    setPlaced(newPlaced);
    setPool([...pool, token]);
  }

  function check(): void {
    const joined = placed.join(' ');
    const correct = joined === correctTokens.join(' ');
    const elapsedMs = Date.now() - startTime.current;
    const g = grade({
      correct,
      elapsedMs,
      hintUsed: showHint && masteryLvl > 0,
      errorCount,
      fastThresholdMs: 8000 + correctTokens.length * 600,
    });
    submitGrade(g);

    if (correct) {
      setStatus('correct');
      setProgressBoost(true);
      markComplete();
      bumpStreak();
      if (g === 'perfect' || g === 'great') bumpMastery();
    } else {
      setStatus('wrong');
      setErrorCount(errorCount + 1);
      recordMistake({ errorType: 'order', userInput: joined });
    }
  }

  function resetPuzzle(): void {
    setPool(shuffle([...correctTokens, ...distractors]));
    setPlaced([]);
    setStatus('pending');
    startTime.current = Date.now();
  }

  return (
    <>
      <GameHud />
      <div className="space-y-8 scanlines relative pb-4">
        <StudyHeader
          courseId={course.id} courseTitle={course.title}
          lessonTitle={lesson.title} sentenceId={sentence.id}
          mode="puzzle"
          index={index} total={total} progressPct={progressPct}
        />

        <GlassCard>
          <div className="text-xs uppercase tracking-[0.18em] text-honey-300 font-arcade mb-3 flex items-center gap-2">
            <span className="h-px w-6 bg-honey-400/60" />
            中译英 · 点单词问 AI · 拼出正确句子
          </div>
          <div className="font-display text-2xl md:text-4xl text-white text-balance leading-tight">
            {sentence.zh}
          </div>

          {/* Placed slots */}
          <div className="mt-8 min-h-[88px] rounded-2xl border border-dashed border-honey-400/30 bg-honey-400/[0.02] p-4 flex flex-wrap gap-2 items-start">
            {placed.length === 0 && (
              <div className="text-xs text-cream-400/60 self-center w-full text-center font-arcade tracking-widest">
                TAP TILES TO BUILD ↓
              </div>
            )}
            <AnimatePresence mode="popLayout" initial={false}>
              {placed.map((t, i) => (
                <motion.button
                  key={`placed-${i}-${t}`}
                  layout
                  initial={{ opacity: 0, scale: 0.7, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.7 }}
                  transition={{ type: 'spring', stiffness: 520, damping: 22 }}
                  onClick={() => returnToPool(t, i)}
                  disabled={status !== 'pending'}
                  className={`px-3.5 py-2 rounded-xl text-base font-medium border tile-drop ${
                    status === 'correct' ? 'bg-mint-500/20 border-mint-400/60 text-mint-100 glow-lime'
                    : status === 'wrong' ? 'bg-ember-500/20 border-ember-400/60 text-ember-100 glow-pink'
                    : 'bg-honey-400/15 border-honey-400/50 text-honey-100 hover:bg-honey-400/30 hover:border-honey-400/80'
                  }`}
                >
                  {t}
                </motion.button>
              ))}
            </AnimatePresence>
          </div>

          {/* Pool */}
          <div className="mt-5 flex flex-wrap gap-2">
            <AnimatePresence mode="popLayout" initial={false}>
              {pool.map((t, i) => (
                <motion.button
                  key={`pool-${i}-${t}`}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={() => pickFromPool(t, i)}
                  disabled={status !== 'pending'}
                  className="px-3.5 py-2 rounded-xl text-base font-medium border bg-ink-800/60 border-white/[0.1] text-cream-100 hover:bg-ink-700/60 hover:border-honey-400/40 transition-colors disabled:opacity-50"
                >
                  {t}
                </motion.button>
              ))}
            </AnimatePresence>
          </div>

          {showHint && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="mt-5 rounded-2xl bg-honey-500/[0.06] border border-honey-500/30 px-4 py-3 text-sm font-mono text-honey-200"
            >
              💡 {sentence.en}
            </motion.div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-3 text-xs font-arcade">
              <button onClick={() => setShowHint(!showHint)} className="text-cream-400 hover:text-honey-300 inline-flex items-center gap-1">
                <Icon name="peek" size="sm" /> {showHint ? 'HIDE' : 'PEEK'}
              </button>
              <button
                onClick={resetPuzzle}
                disabled={status !== 'pending' || placed.length === 0}
                className="text-cream-400 hover:text-honey-300 disabled:opacity-40"
              >
                ↺ RESET
              </button>
              {distractors.length > 0 && (
                <span className="text-ember-300/70">+{distractors.length} 干扰词</span>
              )}
            </div>
            <div className="flex gap-2">
              {status === 'pending' && (
                <GlassButton onClick={check} variant="solid" size="md" disabled={placed.length === 0}>
                  CHECK ✓
                </GlassButton>
              )}
              {status === 'wrong' && (
                <GlassButton onClick={resetPuzzle} variant="solid" size="md">↺ RETRY</GlassButton>
              )}
              {status === 'correct' && (
                <GlassButton onClick={next} variant="solid" size="md">
                  {index + 1 < total ? 'NEXT →' : 'COMPLETE ✨'}
                </GlassButton>
              )}
            </div>
          </div>
        </GlassCard>

        <AnimatePresence mode="wait">
          {status === 'wrong' && (
            <motion.div
              key="wrong-feedback"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="rounded-3xl p-5 border bg-ember-500/[0.08] border-ember-500/30"
            >
              <div className="flex items-center gap-2 mb-3 font-arcade text-ember-300 text-xs tracking-widest">
                <Icon name="warn" size="sm" /> AI 诊断 · 语序问题
              </div>
              <div className="grid gap-2 text-sm">
                <div><span className="text-cream-400">你的：</span><span className="font-mono text-ember-200">{placed.join(' ')}</span></div>
                <div><span className="text-cream-400">正确：</span>
                  <span className="font-mono text-mint-200">
                    <TapToAsk sentence={sentence.en} tone="lime" />
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <ModeSwitcher lessonId={lesson.id} current="puzzle" />
      </div>
    </>
  );
}
